/**
 * Reviews Page Manager
 * Handles the display and functionality of the dedicated reviews page
 */
class ReviewsPageManager {
  constructor(productHandle) {
    this.productHandle = productHandle;
    this.currentSort = "newest";
    this.currentPage = 1;
    this.reviewsPerPage = 10;
    this.allReviews = [];
    this.displayedReviews = [];

    // --- Configuration ---
    this.supabaseUrl = "https://penkozrfaxruujxpotdn.supabase.co";
    this.supabaseKey =
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBlbmtvenJmYXhydXVqeHBvdGRuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTM5NDIyNzYsImV4cCI6MjA2OTUxODI3Nn0.peEO1604jU2dxZD2ZQ1OYLUsxs8sNmK-7MIUQZ8E7Rw";

    // DOM elements
    this.loadingEl = document.getElementById("reviews-loading");
    this.emptyEl = document.getElementById("reviews-empty");
    this.contentEl = document.getElementById("reviews-content");
    this.listEl = document.getElementById("reviews-list");
    this.sortSelect = document.getElementById("reviews-sort");
    this.totalReviewsBadge = document.getElementById("total-reviews-badge");
    this.averageRatingBadge = document.getElementById("average-rating-badge");
    this.loadMoreBtn = document.getElementById("load-more-reviews");

    // Supabase client will be initialized in init method
    this.sbClient = null;
  }

  async init() {
    // Wait for Supabase to be available
    await this.waitForSupabase();

    if (!this.sbClient) {
      console.error("Supabase client not available");
      this.showError();
      return;
    }

    this.attachEventListeners();
    await this.fetchReviews();

    // Handle anchor scrolling after content is loaded
    this.handleAnchorScroll();
  }

  // Helper method to wait for Supabase to load
  async waitForSupabase() {
    let retries = 0;
    const maxRetries = 50; // Wait up to 5 seconds

    while (typeof supabase === "undefined" && retries < maxRetries) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      retries++;
    }

    if (
      typeof supabase !== "undefined" &&
      typeof supabase.createClient === "function"
    ) {
      this.sbClient = supabase.createClient(this.supabaseUrl, this.supabaseKey);
      console.log("Reviews page: Supabase client initialized");
    } else {
      console.error("Reviews page: Supabase failed to load after waiting");
      this.sbClient = null;
    }
  }

  attachEventListeners() {
    this.sortSelect?.addEventListener("change", (e) => {
      this.currentSort = e.target.value;
      this.sortAndRenderReviews();
    });

    this.loadMoreBtn?.addEventListener("click", () => {
      this.loadMoreReviews();
    });
  }

  async fetchReviews() {
    try {
      const { data, error } = await this.sbClient
        .from("reviews")
        .select("*")
        .eq("product_handle", this.productHandle)
        .order("created_at", { ascending: false });

      if (error) throw error;

      this.allReviews = data || [];
      this.sortAndRenderReviews();
      this.updateSummary();
      this.showContent();
    } catch (error) {
      console.error("Error fetching reviews:", error);
      this.showError();
    }
  }

  // Helper method to fetch product information
  async fetchProductInfo() {
    try {
      const response = await fetch(`/products/${this.productHandle}.js`);
      if (response.ok) {
        this.productInfo = await response.json();
        return this.productInfo;
      }
    } catch (error) {
      console.warn("Could not fetch product information:", error);
    }
    return null;
  }

  sortAndRenderReviews() {
    const sortedReviews = this.sortReviews([...this.allReviews]);
    this.displayedReviews = sortedReviews;
    this.currentPage = 1;
    this.renderReviews();
  }

  sortReviews(reviews) {
    switch (this.currentSort) {
      case "newest":
        return reviews.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
      case "oldest":
        return reviews.sort(
          (a, b) => new Date(a.created_at) - new Date(b.created_at)
        );
      case "highest-rated":
        return reviews.sort((a, b) => b.rating - a.rating);
      case "lowest-rated":
        return reviews.sort((a, b) => a.rating - b.rating);
      default:
        return reviews;
    }
  }

  renderReviews() {
    if (!this.displayedReviews.length) {
      this.showEmpty();
      return;
    }

    const reviewsToShow = this.displayedReviews.slice(
      0,
      this.currentPage * this.reviewsPerPage
    );
    this.listEl.innerHTML = "";

    reviewsToShow.forEach((review) => {
      const reviewCard = this.createReviewCard(review);
      this.listEl.appendChild(reviewCard);
    });

    // Show/hide load more button
    const hasMoreReviews = reviewsToShow.length < this.displayedReviews.length;
    this.loadMoreBtn.style.display = hasMoreReviews ? "block" : "none";
  }

  loadMoreReviews() {
    this.currentPage++;
    this.renderReviews();
  }

  createReviewCard(review) {
    const card = document.createElement("div");
    card.className = "review-card";

    // Create unique ID for anchor linking
    const reviewId =
      review.id || review.created_at.replace(/[^a-zA-Z0-9]/g, "");
    card.id = `review-${reviewId}`;

    const formattedDate = new Date(review.created_at).toLocaleDateString(
      "en-US",
      {
        year: "numeric",
        month: "long",
        day: "numeric",
      }
    );

    // Determine if content should be truncated
    const shouldTruncate = review.content.length > 300;
    const truncatedContent = shouldTruncate
      ? review.content.substring(0, 300) + "..."
      : review.content;

    card.innerHTML = `
      <div class="review-card__header">
        <div class="review-card__user">
          <div class="review-card__avatar">
            ${(review.author || "Anonymous").charAt(0).toUpperCase()}
          </div>
          <div class="review-card__user-info">
            <div class="review-card__user-name">
              <h3 class="review-card__name">${review.author || "Anonymous"}</h3>
              ${
                review.verified
                  ? '<span class="review-card__verified">Verified</span>'
                  : ""
              }
            </div>
            <div class="review-card__meta">
              <div class="review-card__rating">
                ${this.renderStars(review.rating)}
              </div>
              <span class="review-card__date">${formattedDate}</span>
            </div>
          </div>
        </div>
        <div class="review-card__product">
          <div class="review-card__product-name">${
            review.product_handle || "Product"
          }</div>
          ${
            review.verified_purchase
              ? '<span class="review-card__purchase-verified">Verified Purchase</span>'
              : ""
          }
        </div>
      </div>
      
      ${
        review.title
          ? `<h4 class="review-card__title">${review.title}</h4>`
          : ""
      }
      
      <div class="review-card__content">
        <p class="review-content" data-full-content="${
          review.content
        }">${truncatedContent}</p>
        ${
          shouldTruncate
            ? '<button class="review-card__show-more" type="button">Show more</button>'
            : ""
        }
      </div>
      
      ${review.media_url ? this.renderReviewImages(review.media_url) : ""}
    `;

    // Don't add individual event listeners - the review-card-interactions.js handles this globally
    return card;
  }

  renderStars(rating) {
    let starsHtml = "";
    for (let i = 1; i <= 5; i++) {
      const isFilled = i <= rating;
      starsHtml += `
        <svg class="review-card__star ${
          isFilled ? "" : "review-card__star--empty"
        }" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      `;
    }
    return starsHtml;
  }

  renderReviewImages(mediaUrl) {
    if (!mediaUrl) return "";

    // Handle single image or multiple images
    const urls = Array.isArray(mediaUrl) ? mediaUrl : [mediaUrl];

    const imagesHtml = urls
      .map(
        (url) => `
      <div class="review-card__image">
        <img src="${url}" alt="Review image" loading="lazy">
      </div>
    `
      )
      .join("");

    return `<div class="review-card__images">${imagesHtml}</div>`;
  }

  // Helper method to unescape HTML
  unescapeHtml(text) {
    const div = document.createElement("div");
    div.innerHTML = text;
    return div.textContent || div.innerText || "";
  }

  updateSummary() {
    const totalReviews = this.allReviews.length;
    const averageRating =
      totalReviews > 0
        ? (
            this.allReviews.reduce((sum, review) => sum + review.rating, 0) /
            totalReviews
          ).toFixed(1)
        : 0;

    this.totalReviewsBadge.textContent = `${totalReviews} review${
      totalReviews !== 1 ? "s" : ""
    }`;
    this.averageRatingBadge.textContent = `${averageRating} ★ average`;
  }

  showContent() {
    this.loadingEl.style.display = "none";
    this.emptyEl.style.display = "none";
    this.contentEl.style.display = "block";
  }

  showEmpty() {
    this.loadingEl.style.display = "none";
    this.contentEl.style.display = "none";
    this.emptyEl.style.display = "block";
  }

  showError() {
    this.loadingEl.style.display = "none";
    this.emptyEl.style.display = "block";
    this.emptyEl.innerHTML = `
      <h3>Unable to load reviews</h3>
      <p>Please try again later.</p>
    `;
  }

  // Handle anchor scrolling to specific reviews
  handleAnchorScroll() {
    const hash = window.location.hash;
    if (hash && hash.startsWith("#review-")) {
      // Wait a bit for the DOM to be fully rendered
      setTimeout(() => {
        const targetElement = document.querySelector(hash);
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          // Add a highlight effect
          targetElement.style.background = "rgba(255, 215, 0, 0.1)";
          targetElement.style.borderColor = "rgba(255, 215, 0, 0.3)";
          setTimeout(() => {
            targetElement.style.background = "";
            targetElement.style.borderColor = "";
          }, 3000);
        }
      }, 500);
    }
  }

  // Helper method to escape HTML
  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}

// Auto-initialize when DOM is loaded
document.addEventListener("DOMContentLoaded", function () {
  // This will be called from the section template
});
